import { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../components/Header";
import {
    Text,
    View,
    Pressable,
    StyleSheet,
    useWindowDimensions,
} from "react-native";
import { useAuthUser } from "../../contexts/useAuthUser";
import { firestore } from "../../services/firebase/index";
import { ActivityIndicator, Provider } from "react-native-paper";
import { Container } from './style';
import { useNavigation } from "@react-navigation/native";
import {
    getDocs,
    query,
    doc,
    documentId,
    collection,
    collectionGroup,
    getDoc,
    updateDoc,
    deleteDoc,
    where,
} from "firebase/firestore";
import { AlertBox } from "../../components/AlertBox";
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { useRemoteConfigContext } from "../../contexts/useRemoteConfigContext";
import { Feather } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import { Badge } from "../AdminPanel/Products/formStyles";
import {
    isLegacyCommunityPost,
    VIRTUAL_GENERAL_CHANNEL_ID,
    VIRTUAL_GENERAL_CHANNEL_NAME,
} from "../../utils/communityChannelUtils";

// Formata a data do comentário com segurança (Timestamp do Firestore, Date ou ausente).
const formatCommentDate = (createdAt) => {
    try {
        const d = createdAt?.toDate
            ? createdAt.toDate()
            : createdAt instanceof Date
                ? createdAt
                : null;
        return d ? d.toLocaleString('pt-BR') : '';
    } catch {
        return '';
    }
};

export function Comments() {
    const { customInfo } = useCustomTheme();
    const navigation = useNavigation();
    const { width } = useWindowDimensions();
    const isMobile = width < 768;

    const [comments, setComments] = useState([]);
    // Identifica a carga corrente: se a tela recarregar no meio de uma carga, a
    // antiga termina depois e não pode sobrescrever a lista nova.
    const loadRunRef = useRef(0);
    // Dados de engajamento da comunidade (nome dos canais e nº de posts por canal).
    const [channelNameById, setChannelNameById] = useState({});
    const [channelPostCounts, setChannelPostCounts] = useState({});
    const [realChannelCount, setRealChannelCount] = useState(0);
    const [activeTab2, setActiveTab2] = useState('catalog'); //divisão entre comentários da comunidade ou do catálogo
    const [activeTab, setActiveTab] = useState('unread'); //divisão entre comentários não lidos e aprovados

    // A comunidade é exclusiva do plano Premium. Esta tela mostrava a aba
    // "Comunidade" para todo mundo, sem checar nada — um cliente do plano
    // Growth via o painel de engajamento de um recurso que não contratou.
    // Exige as duas condições: o plano (a regra comercial) e a flag, que o
    // admin pode desligar mesmo sendo Premium.
    const { hasCommunity } = useRemoteConfigContext();
    const canUseCommunity = hasCommunity && customInfo?.planType === 'premium';
    // Valor DERIVADO, não estado: enquanto o plano e a flag carregam, a origem
    // cai em "catalog" sozinha, sem uma janela em que a aba da comunidade fique
    // ativa e sem depender de um efeito para corrigir depois.
    const commentSource = canUseCommunity ? activeTab2 : 'catalog';
    const [loading, setLoading] = useState(true);
    const { user, authUser } = useAuthUser();
    // `pendingDelete` guarda o comentário aguardando confirmação de exclusão.
    const [showAlert, setShowAlert] = useState({
        visibleAlert: false,
        title: null,
        message: null,
        pendingDelete: null,
    });

    // Executa `worker` sobre os itens com um teto de simultaneidade — mas SEMPRE
    // em paralelo até esse teto. Serializar aqui foi o que deixou a tela mais
    // lenta numa tentativa anterior: agrupar as leituras reduz o número de
    // requisições, mas só ajuda se elas continuarem saindo ao mesmo tempo.
    const mapWithLimit = async (items, limitCount, worker) => {
        const results = new Array(items.length);
        let cursor = 0;

        const runners = Array.from({ length: Math.min(limitCount, items.length) }, async () => {
            while (cursor < items.length) {
                const index = cursor++;
                results[index] = await worker(items[index]);
            }
        });

        await Promise.all(runners);
        return results;
    };

    // Nomes dos alunos em blocos de 30 (`documentId() in [...]`, sem exigir
    // índice), com os blocos disparados em paralelo. Mil alunos distintos saem
    // de mil requisições para ~34, e essas 34 vão em ~3 ondas.
    const fetchUserNames = async (userIds) => {
        const ids = [...new Set(userIds.filter(Boolean))];
        const chunks = [];
        for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));

        const names = new Map();
        await mapWithLimit(chunks, 12, async (chunk) => {
            try {
                const snap = await getDocs(
                    query(collection(firestore, 'users'), where(documentId(), 'in', chunk))
                );
                snap.forEach((d) => {
                    const u = d.data();
                    names.set(d.id, u?.Nome_Completo || u?.Email || 'Aluno sem nome');
                });
            } catch (error) {
                console.error('Erro ao buscar nomes de alunos:', error);
            }
        });

        // Quem não voltou (doc apagado) ainda precisa de um rótulo.
        ids.forEach((id) => { if (!names.has(id)) names.set(id, 'Aluno sem nome'); });
        return names;
    };

    const loadData = async () => {
        const runId = ++loadRunRef.current;
        setLoading(true);
        // Sem comunidade, canais, posts e comentários de post nunca aparecem:
        // não são lidos (posts) nem resolvidos (nomes dos autores).
        const withCommunity = canUseCommunity;
        // try/finally: qualquer falha em uma das três leituras deixava o spinner
        // preso para sempre (setLoading(false) nunca rodava).
        try {

        // Uma única carga (um único loading): comentários (catálogo + comunidade), canais
        // da comunidade e todos os posts (para as métricas de engajamento por canal).
        const [snapshot, channelsSnap, postsSnap] = await Promise.all([
            getDocs(query(collectionGroup(firestore, 'comments'))),
            withCommunity ? getDocs(collection(firestore, 'communityChannels')) : null,
            withCommunity ? getDocs(collection(firestore, 'posts')) : null,
        ]);

        // Todos os posts já vieram no `postsSnap` acima. Antes cada comentário da
        // comunidade fazia um `getDoc` no post — leitura duplicada de dado que já
        // estava em memória.
        const postById = new Map();
        postsSnap?.forEach((d) => postById.set(d.id, d.data()));

        // Passo 1 (sem I/O): monta as linhas com o que já veio nas leituras
        // acima e junta as chaves que ainda precisam ser resolvidas.
        const visibleDocs = withCommunity
            ? snapshot.docs
            : snapshot.docs.filter((docSnap) => !docSnap.ref.path.startsWith('posts/'));
        const base = visibleDocs.map((docSnap) => {
            const commentData = docSnap.data();
            const ids = extractIdsFromPath(docSnap.ref.path);
            // startsWith, não includes: um card/categoria com id terminando em
            // "posts" (ex.: 'blogposts') seria classificado como comunidade.
            const isCommunity = docSnap.ref.path.startsWith('posts/');
            const communityIds = extractIdsFromPathPosts(docSnap.ref.path);

            let postId = null;
            let pageTitle = null;
            let channelId = null;

            if (isCommunity) {
                postId = communityIds?.postId;
                const postData = postById.get(postId) || null;
                pageTitle = postData?.contentText || 'Post da Comunidade';
                // Canal do post (posts legados / sem canal contam como "Geral").
                channelId =
                    postData && !isLegacyCommunityPost(postData)
                        ? String(postData.channelId)
                        : VIRTUAL_GENERAL_CHANNEL_ID;
            }

            return {
                id: docSnap.id,
                ref: docSnap.ref,
                ...commentData,
                pageTitle, // null quando não é comunidade: resolvido no passo 2
                postId,
                isCommunity,
                categoryId: ids?.categoryId ?? null,
                channelId,
                cardId: ids?.cardId ?? null,
                videoId: ids?.videoId ?? null,
            };
        });

        // Passo 2: as três resoluções que faltam, TODAS em paralelo entre si.
        const videoKeys = [...new Set(
            base
                .filter((r) => !r.isCommunity && r.categoryId)
                .map((r) => `${r.categoryId}/${r.cardId}/${r.videoId}`)
        )];

        // O web grava o nome do autor no próprio comentário (`authorName` no
        // catálogo, `userName` na comunidade); só os demais (app mobile,
        // comentários antigos) custam a leitura do doc do aluno.
        const storedName = (r) => r.authorName || r.userName || null;

        const [namesById, categoryTitles, videoTitleList] = await Promise.all([
            fetchUserNames(base.filter((r) => !storedName(r)).map((r) => r.userId)),

            // Uma leitura só da coleção de categorias, em vez de um getDoc por
            // categoria distinta.
            getDocs(collection(firestore, 'categories'))
                .then((snap) => {
                    const map = new Map();
                    snap.forEach((d) => map.set(d.id, d.data()?.title || null));
                    return map;
                })
                .catch(() => new Map()),

            // Títulos de aula moram em subcoleções (um doc por aula), então
            // continuam sendo leituras individuais — mas só das aulas distintas
            // que têm comentário, e não uma por comentário.
            mapWithLimit(videoKeys, 40, async (key) => {
                const [categoryId, cardId, videoId] = key.split('/');
                try {
                    return await getVideoTitle({ categoryId, cardId, videoId });
                } catch {
                    return null;
                }
            }),
        ]);

        const videoTitles = new Map(videoKeys.map((key, i) => [key, videoTitleList[i]]));

        const commentsData = base
            .map((c) => ({
                ...c,
                userName: storedName(c) || namesById.get(c.userId) || 'Aluno sem nome',
                pageTitle:
                    c.pageTitle ||
                    videoTitles.get(`${c.categoryId}/${c.cardId}/${c.videoId}`) ||
                    'Aula',
                categoryName: c.isCommunity
                    ? null
                    : (categoryTitles.get(c.categoryId) || 'Sem categoria'),
            }))
            .sort((a, b) => {
                const aTime = a.createdAt?.seconds ?? 0;
                const bTime = b.createdAt?.seconds ?? 0;
                return bTime - aTime; // desc
            });

        // Canais (id -> nome) e contagem de posts por canal (posts legados = Geral).
        const nameById = {};
        channelsSnap?.forEach((d) => { nameById[d.id] = d.data()?.name || 'Canal'; });

        const postCounts = {};
        postsSnap?.forEach((d) => {
            const pdata = d.data();
            const ch = isLegacyCommunityPost(pdata)
                ? VIRTUAL_GENERAL_CHANNEL_ID
                : String(pdata.channelId);
            postCounts[ch] = (postCounts[ch] || 0) + 1;
        });

        // Carga corrente mudou enquanto esta rodava: descarta o resultado velho.
        if (runId !== loadRunRef.current) return;

        setChannelNameById(nameById);
        setChannelPostCounts(postCounts);
        setRealChannelCount(channelsSnap?.size ?? 0);
        setComments(commentsData);

        } catch (error) {
            console.error('Erro ao carregar comentários:', error);
        } finally {
            setLoading(false);
        }
    };

    // Sessão já resolvida no boot (ver AuthUserProvider).
    useEffect(() => {
        if (!authUser) {
            setLoading(false);
            return;
        }
        loadData();
    }, [authUser, canUseCommunity]);

    const toggleApproveComment = async (comment) => {
        await updateDoc(comment.ref, {
            approved: true,
            read: true,
        });

        setComments(prev =>
            prev.map(c =>
                c.id === comment.id
                    ? { ...c, approved: true, read: true }
                    : c
            )
        );
    };

    const deleteComment = async (comment) => {
        try {
            await deleteDoc(comment.ref);

            // Remove o comentário do state
            setComments(prev => prev.filter(c => c.id !== comment.id));
        } catch (error) {
            console.error("Erro ao excluir comentário:", error);
        }
    };

    const requestDeleteComment = (comment) => {
        setShowAlert({
            visibleAlert: true,
            title: 'Excluir comentário?',
            message: 'Esta ação é permanente e não pode ser desfeita.',
            pendingDelete: comment,
        });
    };

    const hideAlert = () => {
        setShowAlert({
            visibleAlert: false,
            title: null,
            message: null,
            pendingDelete: null,
        });
    };

    const extractIdsFromPath = (path) => {
        if (!path || typeof path !== 'string') {
            console.error('Path inválido:', path);
            return null;
        }

        const parts = path.split('/');

        if (parts.length < 6) {
            console.error('Path incompleto:', path);
            return null;
        }

        return {
            categoryId: parts[1],
            cardId: parts[3],
            videoId: parts[5],
        };
    };

    const extractIdsFromPathPosts = (path) => {
        if (!path || typeof path !== 'string') return null;

        const parts = path.split('/');

        // posts/{postId}/comments/{commentId}
        if (parts.length < 4) return null;

        return {
            postId: parts[1],
        };
    };

    const getVideoTitle = async ({ categoryId, cardId, videoId }) => {
        const videoRef = doc(
            firestore,
            'categories',
            categoryId,
            'cards',
            cardId,
            'videos',
            videoId
        );

        const videoSnap = await getDoc(videoRef);
        if (!videoSnap.exists()) return 'Aula';
        return videoSnap.data()?.title ?? 'Aula';
    };

    const getCommentType = (comment) => {
        if (comment.isCommunity) return 'community';
        return 'catalog';
    };

    // Abre o conteúdo na MESMA aba, navegando dentro do app — que já está
    // logado. (Aba nova já deu "você não possui acesso": a sessão do Firebase é
    // por domínio, e o link ia para o `webUrl`, não para onde o admin estava.)
    //
    // `fromComments` faz o "Voltar" da aula/post retornar a esta lista; sem
    // ele, a aula volta para o módulo e o post, para o feed. A tela fica
    // montada no menu, então a volta não recarrega os comentários.
    const openCommentLink = (comment) => {
        if (getCommentType(comment) === 'community') {
            navigation.navigate('CommunityComments', { id: comment.postId, fromComments: true });
            return;
        }
        navigation.navigate('Aula', {
            categoryId: comment.categoryId,
            cardId: comment.cardId,
            selectedVideo: comment.videoId,
            fromComments: true,
        });
    };


    const commentsByType = comments.filter(
        (comment) => getCommentType(comment) === commentSource
    );

    const unreadComments = commentsByType.filter(
        c => !c.read && !c.approved
    );

    const approvedComments = commentsByType.filter(
        c => c.approved
    );

    const visibleComments =
        activeTab === 'unread'
            ? unreadComments
            : approvedComments;

    // Top 5 categorias do catálogo por nº de comentários (independente de lido/não lido).
    const catalogStats = useMemo(() => {
        const counts = {};
        comments.forEach((c) => {
            if (!c.isCommunity && c.categoryId) {
                const name = c.categoryName || 'Sem categoria';
                counts[name] = (counts[name] || 0) + 1;
            }
        });
        return Object.entries(counts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [comments]);

    // Engajamento por canal da comunidade: posts + comentários. Inclui sempre o "Geral"
    // e omite canais sem qualquer atividade.
    const communityStats = useMemo(() => {
        const commentCounts = {};
        comments.forEach((c) => {
            if (c.isCommunity) {
                const ch = c.channelId || VIRTUAL_GENERAL_CHANNEL_ID;
                commentCounts[ch] = (commentCounts[ch] || 0) + 1;
            }
        });

        const ids = new Set([
            VIRTUAL_GENERAL_CHANNEL_ID,
            ...Object.keys(channelNameById),
            ...Object.keys(channelPostCounts),
            ...Object.keys(commentCounts),
        ]);

        const rows = [];
        ids.forEach((id) => {
            const posts = channelPostCounts[id] || 0;
            const commentsCount = commentCounts[id] || 0;
            const isGeneral = id === VIRTUAL_GENERAL_CHANNEL_ID;
            const isExistingChannel = Object.prototype.hasOwnProperty.call(channelNameById, id);
            // Sempre mostra o Geral e os canais existentes (mesmo sem atividade, para
            // permitir a comparação); ignora apenas ids órfãos/removidos sem atividade.
            if (!isGeneral && !isExistingChannel && posts === 0 && commentsCount === 0) return;
            const name = isGeneral
                ? VIRTUAL_GENERAL_CHANNEL_NAME
                : (channelNameById[id] || 'Canal removido');
            rows.push({ id, name, posts, comments: commentsCount });
        });

        return rows.sort((a, b) => (b.posts + b.comments) - (a.posts + a.comments));
    }, [comments, channelNameById, channelPostCounts]);

    // "Mais de um canal" = existe ao menos um canal real além do "Geral" virtual.
    const hasMultipleChannels = realChannelCount >= 1;

    // Pílula de aba (segmented control) reaproveitada para origem e status.
    const Segment = ({ active, onPress, label, count }) => (
        <Pressable
            onPress={onPress}
            style={({ hovered }) => [
                styles.segment,
                active && styles.segmentActive,
                hovered && !active && { backgroundColor: ADMIN_COLORS.surface },
            ]}
        >
            <Text style={[styles.segmentText, active && styles.segmentTextActive]}>{label}</Text>
            {typeof count === 'number' && (
                <View style={[styles.countPill, active && styles.countPillActive]}>
                    <Text style={[styles.countText, active && styles.countTextActive]}>{count}</Text>
                </View>
            )}
        </Pressable>
    );

    return (
        <Provider>
            <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
                <Header navigation={navigation} user={user} isAdminPage />

                <Container>
                    <View style={styles.page}>
                        {/* Cabeçalho da página */}
                        <View style={styles.headerBlock}>
                            <View style={{ flex: 1, minWidth: 0 }}>
                                <Text style={[styles.title, isMobile && { fontSize: 24 }]}>Comentários</Text>
                                <Text style={styles.subtitle}>
                                    {canUseCommunity
                                        ? 'Modere os comentários do catálogo e da comunidade.'
                                        : 'Modere os comentários do catálogo.'}
                                </Text>
                            </View>
                            {/* A tela fica montada no menu e carrega uma vez só:
                                sem isto, comentário novo só aparecia recarregando
                                a página inteira. */}
                            <Pressable
                                onPress={loadData}
                                disabled={loading}
                                style={({ hovered }) => [
                                    styles.actionBtn,
                                    styles.deleteBtn,
                                    hovered && !loading && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                                    loading && { opacity: 0.5 },
                                ]}
                            >
                                <Feather name="refresh-cw" size={14} color={ADMIN_COLORS.ink} />
                                <Text style={[styles.actionText, { color: ADMIN_COLORS.ink }]}>Atualizar</Text>
                            </Pressable>
                        </View>

                        {loading ? (
                            <View style={styles.loadingBox}>
                                <ActivityIndicator color={ADMIN_COLORS.teal} size="large" />
                            </View>
                        ) : (
                          <>
                        {/* Barra de filtros: origem + status */}
                        <View style={styles.toolbar}>
                            {/* Sem comunidade, a origem é sempre o catálogo: o par
                                de botões vira um rótulo sem escolha, então some. */}
                            {canUseCommunity && (
                                <View style={styles.segmentGroup}>
                                    <Segment
                                        active={commentSource === 'catalog'}
                                        onPress={() => setActiveTab2('catalog')}
                                        label="Catálogo"
                                    />
                                    <Segment
                                        active={commentSource === 'community'}
                                        onPress={() => setActiveTab2('community')}
                                        label="Comunidade"
                                    />
                                </View>
                            )}

                            {/* `marginLeft: auto` mantém os filtros de status à
                                direita: o `space-between` da toolbar só alinha
                                assim quando existem dois grupos, e sem a
                                comunidade este fica sozinho. */}
                            <View style={[styles.segmentGroup, { marginLeft: 'auto' }]}>
                                <Segment
                                    active={activeTab === 'unread'}
                                    onPress={() => setActiveTab('unread')}
                                    label="Não lidos"
                                    count={unreadComments.length}
                                />
                                <Segment
                                    active={activeTab === 'approved'}
                                    onPress={() => setActiveTab('approved')}
                                    label="Lidos"
                                    count={approvedComments.length}
                                />
                            </View>
                        </View>

                        {/* Painel de análise conforme a origem selecionada */}
                        {commentSource === 'catalog' ? (
                            <View style={styles.analyticsCard}>
                                <Text style={styles.analyticsTitle}>Categorias com mais comentários</Text>
                                <Text style={styles.analyticsSub}>
                                    Top 5 categorias do catálogo por volume de comentários.
                                </Text>
                                {catalogStats.length === 0 ? (
                                    <Text style={styles.analyticsEmpty}>Ainda não há comentários no catálogo.</Text>
                                ) : (
                                    <View style={{ marginTop: 8 }}>
                                        {catalogStats.map((row, i) => (
                                            <View
                                                key={`${row.name}-${i}`}
                                                style={[styles.rankRow, i > 0 && styles.rankRowDivider]}
                                            >
                                                <View style={styles.rankIndex}>
                                                    <Text style={styles.rankIndexText}>{i + 1}</Text>
                                                </View>
                                                <Text style={styles.rankName} numberOfLines={1}>{row.name}</Text>
                                                <View style={styles.rankCountPill}>
                                                    <Text style={styles.rankCountText}>{row.count}</Text>
                                                </View>
                                            </View>
                                        ))}
                                    </View>
                                )}
                            </View>
                        ) : (
                            <View style={styles.analyticsCard}>
                                <Text style={styles.analyticsTitle}>
                                    {hasMultipleChannels ? 'Engajamento por canal' : 'Engajamento da comunidade'}
                                </Text>
                                <Text style={styles.analyticsSub}>
                                    {hasMultipleChannels
                                        ? 'Posts e comentários em cada canal da comunidade.'
                                        : 'Posts e comentários do canal Geral.'}
                                </Text>
                                {communityStats.length === 0 ? (
                                    <Text style={styles.analyticsEmpty}>Ainda não há atividade na comunidade.</Text>
                                ) : (
                                    <View style={styles.channelGrid}>
                                        {communityStats.map((ch) => (
                                            <View key={ch.id} style={styles.channelCard}>
                                                <Text style={styles.channelName} numberOfLines={1}>{ch.name}</Text>
                                                <View style={styles.channelMetrics}>
                                                    <View style={styles.metric}>
                                                        <Text style={styles.metricValue}>{ch.posts}</Text>
                                                        <Text style={styles.metricLabel}>Posts</Text>
                                                    </View>
                                                    <View style={styles.metric}>
                                                        <Text style={styles.metricValue}>{ch.comments}</Text>
                                                        <Text style={styles.metricLabel}>Comentários</Text>
                                                    </View>
                                                </View>
                                            </View>
                                        ))}
                                    </View>
                                )}
                            </View>
                        )}

                        {/* Lista de comentários */}
                        <View style={styles.list}>
                            {visibleComments.map((comment) => {
                                const initial = (comment.userName || '?').trim().charAt(0).toUpperCase();
                                return (
                                    <View key={comment.id} style={styles.card}>
                                        <View style={styles.cardTop}>
                                            <View style={styles.avatar}>
                                                <Text style={styles.avatarText}>{initial}</Text>
                                            </View>
                                            <View style={{ flex: 1, minWidth: 0 }}>
                                                <Text style={styles.author} numberOfLines={1}>
                                                    {comment.userName}
                                                </Text>
                                                <Text style={styles.date}>{formatCommentDate(comment.createdAt)}</Text>
                                            </View>
                                            <View style={styles.badges}>
                                                <Badge variant={comment.approved ? 'success' : 'info'}>
                                                    {comment.approved ? 'Lido' : 'Não lido'}
                                                </Badge>
                                            </View>
                                        </View>

                                        <Text style={styles.context}>
                                            Comentou em{' '}
                                            <Text
                                                style={styles.contextLink}
                                                onPress={() => openCommentLink(comment)}
                                                numberOfLines={3}
                                                ellipsizeMode="tail"
                                            >
                                                {comment.pageTitle}
                                            </Text>
                                        </Text>

                                        <Text style={styles.commentText}>{comment.comment}</Text>

                                        <View style={styles.cardActions}>
                                            {!comment.approved && (
                                                <Pressable
                                                    onPress={() => toggleApproveComment(comment)}
                                                    style={({ hovered }) => [
                                                        styles.actionBtn,
                                                        styles.approveBtn,
                                                        hovered && { backgroundColor: '#D1FAE5' },
                                                    ]}
                                                >
                                                    <Feather name="check" size={14} color={ADMIN_COLORS.success} />
                                                    <Text style={[styles.actionText, { color: ADMIN_COLORS.success }]}>
                                                        Marcar como lido
                                                    </Text>
                                                </Pressable>
                                            )}

                                            <Pressable
                                                onPress={() => requestDeleteComment(comment)}
                                                style={({ hovered }) => [
                                                    styles.actionBtn,
                                                    styles.deleteBtn,
                                                    hovered && { backgroundColor: ADMIN_COLORS.dangerSoft },
                                                ]}
                                            >
                                                <Feather name="trash-2" size={14} color={ADMIN_COLORS.danger} />
                                                <Text style={[styles.actionText, { color: ADMIN_COLORS.danger }]}>
                                                    Excluir
                                                </Text>
                                            </Pressable>
                                        </View>
                                    </View>
                                );
                            })}

                            {visibleComments.length === 0 && (
                                <View style={styles.emptyState}>
                                    <Feather name="message-square" size={28} color={ADMIN_COLORS.placeholder} />
                                    <Text style={styles.emptyTitle}>
                                        Nenhum comentário {activeTab === 'unread' ? 'não lido' : 'lido'}
                                    </Text>
                                    <Text style={styles.emptySub}>
                                        Os comentários {commentSource === 'catalog' ? 'do catálogo' : 'da comunidade'} aparecerão aqui.
                                    </Text>
                                </View>
                            )}
                        </View>
                          </>
                        )}
                    </View>
                </Container>

                <AlertBox variant="admin"
                    title={showAlert.title}
                    message={showAlert.message}
                    visible={showAlert.visibleAlert}
                    onClose={hideAlert}
                    leftButton={showAlert.pendingDelete ? 'Cancelar' : 'OK'}
                    leftButtonFunction={hideAlert}
                    rightButton={showAlert.pendingDelete ? 'Excluir' : undefined}
                    rightButtonFunction={() => {
                        if (showAlert.pendingDelete) deleteComment(showAlert.pendingDelete);
                    }}
                />
            </View>
        </Provider>
    );
}

const styles = StyleSheet.create({
    page: {
        width: '100%',
        maxWidth: 960,
        alignSelf: 'center',
    },
    headerBlock: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 20,
    },
    title: {
        fontFamily: ADMIN_FONT,
        fontSize: 28,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    subtitle: {
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        color: ADMIN_COLORS.muted,
        marginTop: 4,
    },
    toolbar: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    segmentGroup: {
        flexDirection: 'row',
        backgroundColor: ADMIN_COLORS.secondaryBg,
        borderRadius: 10,
        padding: 4,
        gap: 4,
    },
    segment: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderRadius: 8,
        cursor: 'pointer',
    },
    segmentActive: {
        backgroundColor: ADMIN_COLORS.surface,
        boxShadow: '0 1px 2px rgba(16,24,40,0.08)',
    },
    segmentText: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '600',
        color: ADMIN_COLORS.muted,
    },
    segmentTextActive: {
        color: ADMIN_COLORS.ink,
    },
    countPill: {
        minWidth: 20,
        paddingHorizontal: 6,
        paddingVertical: 1,
        borderRadius: 999,
        backgroundColor: ADMIN_COLORS.border,
        alignItems: 'center',
        justifyContent: 'center',
    },
    countPillActive: {
        backgroundColor: ADMIN_COLORS.accentSoft,
    },
    countText: {
        fontFamily: ADMIN_FONT,
        fontSize: 11,
        fontWeight: '700',
        color: ADMIN_COLORS.muted,
    },
    countTextActive: {
        color: ADMIN_COLORS.accentDark,
    },
    list: {
        gap: 14,
    },
    loadingBox: {
        paddingVertical: 80,
        alignItems: 'center',
        justifyContent: 'center',
    },
    analyticsCard: {
        backgroundColor: ADMIN_COLORS.surface,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: ADMIN_COLORS.radius,
        padding: 16,
        marginBottom: 20,
        boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    },
    analyticsTitle: {
        fontFamily: ADMIN_FONT,
        fontSize: 15,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    analyticsSub: {
        fontFamily: ADMIN_FONT,
        fontSize: 12.5,
        color: ADMIN_COLORS.muted,
        marginTop: 2,
    },
    analyticsEmpty: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.muted,
        marginTop: 12,
    },
    // Ranking de categorias (catálogo)
    rankRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 11,
    },
    rankRowDivider: {
        borderTopWidth: 1,
        borderTopColor: ADMIN_COLORS.border,
    },
    rankIndex: {
        width: 24,
        height: 24,
        borderRadius: 999,
        backgroundColor: ADMIN_COLORS.accentSoft,
        alignItems: 'center',
        justifyContent: 'center',
    },
    rankIndexText: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        fontWeight: '700',
        color: ADMIN_COLORS.accentDark,
    },
    rankName: {
        fontFamily: ADMIN_FONT,
        fontSize: 13.5,
        fontWeight: '600',
        color: ADMIN_COLORS.ink,
        flex: 1,
        minWidth: 0,
    },
    rankCountPill: {
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 999,
        backgroundColor: ADMIN_COLORS.surfaceSubtle,
    },
    rankCountText: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    // Engajamento por canal (comunidade)
    channelGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 12,
    },
    channelCard: {
        flexGrow: 1,
        flexBasis: 180,
        minWidth: 150,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 10,
        padding: 12,
        backgroundColor: ADMIN_COLORS.surfaceSubtle,
    },
    channelName: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
        marginBottom: 8,
    },
    channelMetrics: {
        flexDirection: 'row',
        gap: 20,
    },
    metric: {},
    metricValue: {
        fontFamily: ADMIN_FONT,
        fontSize: 20,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    metricLabel: {
        fontFamily: ADMIN_FONT,
        fontSize: 11,
        color: ADMIN_COLORS.muted,
        marginTop: 1,
    },
    card: {
        backgroundColor: ADMIN_COLORS.surface,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: ADMIN_COLORS.radius,
        padding: 16,
        boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    },
    cardTop: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
    },
    avatar: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: ADMIN_COLORS.tealSoft,
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarText: {
        fontFamily: ADMIN_FONT,
        fontSize: 16,
        fontWeight: '700',
        color: ADMIN_COLORS.teal,
    },
    author: {
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    date: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        color: ADMIN_COLORS.muted,
        marginTop: 2,
    },
    badges: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 6,
    },
    context: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.muted,
        marginBottom: 8,
    },
    contextLink: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '600',
        color: ADMIN_COLORS.accentDark,
        textDecorationLine: 'underline',
    },
    commentText: {
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        lineHeight: 20,
        color: ADMIN_COLORS.label,
    },
    cardActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 14,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: ADMIN_COLORS.border,
    },
    actionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 7,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: 1,
        cursor: 'pointer',
    },
    approveBtn: {
        backgroundColor: ADMIN_COLORS.successSoft,
        borderColor: ADMIN_COLORS.successBorder,
    },
    deleteBtn: {
        backgroundColor: ADMIN_COLORS.surface,
        borderColor: ADMIN_COLORS.border,
    },
    actionText: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '600',
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
        gap: 8,
    },
    emptyTitle: {
        fontFamily: ADMIN_FONT,
        fontSize: 15,
        fontWeight: '600',
        color: ADMIN_COLORS.ink,
        marginTop: 4,
    },
    emptySub: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.muted,
        textAlign: 'center',
    },
});
